import React, { memo, forwardRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AgentLogo from '../common/logo/AgentLogo';
import AgentBadges from './AgentBadges';
import Button from '../common/button/Button';
import TooltipPortal from '../common/tooltip/TooltipPortal';

import styles from './styles/AgentCard.module.css';

const AgentCard = forwardRef(({ agent, mode = 'owned' }, ref) => {
    const navigate = useNavigate();
    const [hoveredTooltip, setHoveredTooltip] = useState(null);

    const goToDetails = useCallback(() => {
        navigate(`/agents/${agent.slug}`);
    }, [navigate, agent.slug]);

    const startNewChat = useCallback((e) => {
        e.stopPropagation();
        navigate(`/agent/${agent.slug}`);
    }, [navigate, agent.slug]);

    return (
        <motion.div
            ref={ref}
            className={styles.card}
            onClick={goToDetails}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{
                y: -2,
                transition: { duration: 0.2, ease: "easeOut" },
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            <div className={styles.content}>
                <motion.div
                    className={styles.header}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                >
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                    >
                        <AgentLogo logo={agent.logo} name={agent.name} size="large" />
                    </motion.div>
                    <div className={styles.titleBlock}>
                        <div className={styles.titleRow}>
                            <h3 className={styles.title}>{agent.name}</h3>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.25, delay: 0.15 }}
                            >
                                <AgentBadges isActive={agent.isActive} accessType={agent.accessType} />
                            </motion.div>
                        </div>
                        <p className={styles.slug}>@{agent.slug}</p>
                        {agent.description && (
                            <motion.p
                                className={styles.description}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.35, delay: 0.2 }}
                            >
                                {agent.description}
                            </motion.p>
                        )}
                    </div>
                </motion.div>
            </div>

            <div className={styles.rightRow} onClick={e => e.stopPropagation()}>
                <div className={styles.stats}>
                    {mode === 'owned' ? (
                        <>
                            <div onMouseEnter={() => setHoveredTooltip('today')} onMouseLeave={() => setHoveredTooltip(null)}>
                                <TooltipPortal tooltip={`${agent.stats?.chatsToday ?? 0} chats started today`} show={hoveredTooltip === 'today'}>
                                    <div className={styles.statItem}>
                                        <span className={styles.statValue}>{agent.stats?.chatsToday ?? 0}</span>
                                        <span className={styles.statLabel}>Today</span>
                                    </div>
                                </TooltipPortal>
                            </div>
                            <div onMouseEnter={() => setHoveredTooltip('chats')} onMouseLeave={() => setHoveredTooltip(null)}>
                                <TooltipPortal tooltip={`${agent.stats?.chatsTotal ?? 0} total chats with this agent`} show={hoveredTooltip === 'chats'}>
                                    <div className={styles.statItem}>
                                        <span className={styles.statValue}>{agent.stats?.chatsTotal ?? 0}</span>
                                        <span className={styles.statLabel}>Chats</span>
                                    </div>
                                </TooltipPortal>
                            </div>
                            <div onMouseEnter={() => setHoveredTooltip('services')} onMouseLeave={() => setHoveredTooltip(null)}>
                                <TooltipPortal tooltip={`${agent.stats?.enabledServicesCount ?? 0} enabled out of ${agent.stats?.servicesCount ?? 0} total services`} show={hoveredTooltip === 'services'}>
                                    <div className={styles.statItem}>
                                        <span className={styles.statValue}>
                                            {agent.stats?.enabledServicesCount ?? 0}/{agent.stats?.servicesCount ?? 0}
                                        </span>
                                        <span className={styles.statLabel}>Services</span>
                                    </div>
                                </TooltipPortal>
                            </div>
                        </>
                    ) : (
                        <>
                            <div onMouseEnter={() => setHoveredTooltip('user_today')} onMouseLeave={() => setHoveredTooltip(null)}>
                                <TooltipPortal tooltip={`${agent.userChatsToday ?? 0} chats started today with this agent`} show={hoveredTooltip === 'user_today'}>
                                    <div className={styles.statItem}>
                                        <span className={styles.statValue}>{agent.userChatsToday ?? 0}</span>
                                        <span className={styles.statLabel}>Today</span>
                                    </div>
                                </TooltipPortal>
                            </div>
                            <div onMouseEnter={() => setHoveredTooltip('user_chats')} onMouseLeave={() => setHoveredTooltip(null)}>
                                <TooltipPortal tooltip={`${agent.userChatsTotal ?? 0} total chats you've had with this agent`} show={hoveredTooltip === 'user_chats'}>
                                    <div className={styles.statItem}>
                                        <span className={styles.statValue}>{agent.userChatsTotal ?? 0}</span>
                                        <span className={styles.statLabel}>My chats</span>
                                    </div>
                                </TooltipPortal>
                            </div>
                        </>
                    )}
                </div>

                <div className={styles.actions}>
                    <Button size="small" variant="primary" onClick={startNewChat} title="Start a new chat">
                        New chat
                    </Button>
                    <Button size="small" variant="outline" onClick={e => { e.stopPropagation(); goToDetails(); }} title="View agent details">
                        Details
                    </Button>
                </div>
            </div>
        </motion.div>
    );
});

AgentCard.displayName = 'AgentCard';

export default memo(AgentCard);
